import { Button } from "@pej/kore/components";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { AssetFolderSummary, FolderScopeValue } from "./asset-types";
import { sameScope } from "./asset-types";
import { fitChips } from "./fit-chips";
import { CHIP_CARET_CLASS, CHIP_GROUP_CLASS, CHIP_LABEL_CLASS, FolderChip } from "./folder-chip";

export interface FolderScopeProps {
    folders: AssetFolderSummary[];
    scope: FolderScopeValue;
    canManage: boolean;
    onScopeChange: (scope: FolderScopeValue) => void;
    onOpenFolderMenu: () => void;
    onRenameFolder: (folder: AssetFolderSummary) => void;
    onDeleteFolder: (folder: AssetFolderSummary) => void;
}

// gap-1.5 on the row below, expressed as a number because fitChips needs one.
const GAP_PX = 6;

/**
 * The selected folder promoted to the front, then the rest alphabetically, so a
 * choice made in the folder menu never disappears when the menu closes.
 */
function buildCandidates(folders: AssetFolderSummary[], scope: FolderScopeValue): AssetFolderSummary[] {
    if (scope.mode !== "folder") return [...folders].sort((a, b) => a.name.localeCompare(b.name));
    const selected = folders.filter((folder) => folder.id === scope.folderId);
    const rest = folders.filter((folder) => folder.id !== scope.folderId).sort((a, b) => a.name.localeCompare(b.name));
    return [...selected, ...rest];
}

/**
 * The folder scope row (spec §5.2): `All` · `Unsorted` · folder chips · an
 * overflow chip, on one line, always.
 *
 * Scope is single-select — choosing anything replaces the current scope rather
 * than adding to it. The wire would now accept "unsorted or in Brand", and the
 * row deliberately does not offer it: a union is a second mental model bought
 * for a library of at most a hundred assets, where free-text search already
 * reaches across folders.
 *
 * The overflow chip is unconditional. It reads `+N` when folders overflow and
 * `Folders` when they do not, because it is the only route to the folder menu
 * and therefore to folder creation — conditional on overflow, a workspace with
 * two folders could never make a third.
 */
export function FolderScope({
    folders,
    scope,
    canManage,
    onScopeChange,
    onOpenFolderMenu,
    onRenameFolder,
    onDeleteFolder,
}: FolderScopeProps) {
    const candidates = useMemo(() => buildCandidates(folders, scope), [folders, scope]);

    // Optimistic default: show every candidate until the row is measured.
    // `recalculate` treats a zero-width reading as "not measured yet" rather
    // than "genuinely no room", so this is what renders until the first real
    // measurement lands.
    const [visibleCount, setVisibleCount] = useState(candidates.length);
    // Whether `recalculate` has ever measured a real width. `visibleCount` is
    // sampled once, at first render — if `folders` arrives after that (a host
    // fetching it asynchronously, as the picker does), the sample is 0 and
    // `recalculate` never gets a chance to correct it upward, because it does
    // nothing while it can't measure a real width. Gating the render on this
    // flag rather than trusting `visibleCount` alone is what actually delivers
    // the optimistic default above, instead of only documenting it.
    const [measured, setMeasured] = useState(false);

    const containerRef = useRef<HTMLDivElement>(null);
    const sizerRef = useRef<HTMLDivElement>(null);

    // Reads live DOM refs rather than closing over props, so this one stable
    // callback is correct from the mount effect, from a later prop change, and
    // from the ResizeObserver alike — there is no stale closure it could serve.
    const recalculate = useCallback(() => {
        const container = containerRef.current;
        const sizer = sizerRef.current;
        if (!container || !sizer) return;

        const available = container.clientWidth;
        if (available <= 0) return;

        const children = Array.from(sizer.children) as HTMLElement[];
        // Sizer layout: [All, Unsorted, ...candidate chips, overflow "+N",
        // overflow "Folders"]. The live chip renders "+N" only when something
        // actually overflows and the bare, wider "Folders" label otherwise
        // (see the docblock above) — reserving only the narrow variant is
        // what let the everything-fits row clip its own overflow chip (I3,
        // final review). Reserving the wider of the two closes that gap.
        const allWidth = children[0]?.offsetWidth ?? 0;
        const unsortedWidth = children[1]?.offsetWidth ?? 0;
        const overflowCountWidth = children[children.length - 2]?.offsetWidth ?? 0;
        const overflowLabelWidth = children[children.length - 1]?.offsetWidth ?? 0;
        const overflowWidth = Math.max(overflowCountWidth, overflowLabelWidth);
        const folderWidths = children.slice(2, children.length - 2).map((el) => el.offsetWidth);

        // The overflow chip is always reserved because it is always rendered.
        const reserved = allWidth + GAP_PX + unsortedWidth + GAP_PX + overflowWidth + GAP_PX;
        setVisibleCount(fitChips(folderWidths, available - reserved, GAP_PX));
        setMeasured(true);
    }, []);

    // biome-ignore lint/correctness/useExhaustiveDependencies: candidates drives when to re-measure, not what recalculate reads
    useLayoutEffect(() => {
        recalculate();
    }, [recalculate, candidates]);

    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const observer = new ResizeObserver(recalculate);
        observer.observe(container);
        return () => observer.disconnect();
    }, [recalculate]);

    // Unmeasured, every candidate shows — the optimistic default the docblock
    // above promises. Sampling `visibleCount` once at first render and trusting
    // it forever would instead lock in whatever `candidates.length` happened to
    // be at that moment (zero, if a host fetches `folders` asynchronously) and
    // never correct upward, since `recalculate` does nothing until it can
    // measure a real width.
    const visibleFolders = measured ? candidates.slice(0, visibleCount) : candidates;
    const hiddenCount = candidates.length - visibleFolders.length;

    return (
        <div className="flex flex-col gap-1.5">
            {/* Off-screen sizer: every candidate plus both overflow chip
                variants — "+N" and the bare "Folders" label the live chip
                falls back to when nothing overflows — laid out via
                `visibility:hidden` (real width, nothing painted, out of tab
                order) rather than `display:none` (which reports 0 for
                everything). Both variants are measured because whichever
                renders live depends on `hiddenCount`, which isn't known until
                after this same measurement runs (I3, final review).
                `aria-hidden` keeps every duplicate out of the accessibility
                tree so role queries only see the live row. The folder entries
                are static replicas built from FolderChip's own exported
                classes — rendering FolderChip here would mount a
                DropdownMenu root per folder just to measure it. */}
            <div
                ref={sizerRef}
                data-testid="folder-scope-sizer"
                className="pointer-events-none invisible fixed top-0 flex gap-1.5"
                style={{ left: "-9999px" }}
                aria-hidden="true"
            >
                <Button type="button" variant="outline" size="sm">
                    All
                </Button>
                <Button type="button" variant="outline" size="sm">
                    Unsorted
                </Button>
                {candidates.map((folder) => (
                    <div key={folder.id} className={CHIP_GROUP_CLASS}>
                        <span className={CHIP_LABEL_CLASS}>{folder.name}</span>
                        {canManage && <span className={CHIP_CARET_CLASS} />}
                    </div>
                ))}
                <Button type="button" variant="outline" size="sm">
                    +{candidates.length}
                </Button>
                <Button type="button" variant="outline" size="sm">
                    Folders
                </Button>
            </div>

            <div
                ref={containerRef}
                data-testid="folder-scope-row"
                className="flex flex-nowrap items-center gap-1.5 overflow-hidden"
            >
                <Button
                    type="button"
                    variant={scope.mode === "all" ? "default" : "outline"}
                    size="sm"
                    aria-pressed={scope.mode === "all"}
                    onClick={() => onScopeChange({ mode: "all" })}
                >
                    All
                </Button>
                <Button
                    type="button"
                    variant={scope.mode === "unsorted" ? "default" : "outline"}
                    size="sm"
                    aria-pressed={scope.mode === "unsorted"}
                    onClick={() => onScopeChange({ mode: "unsorted" })}
                >
                    Unsorted
                </Button>
                {visibleFolders.map((folder) => (
                    <FolderChip
                        key={folder.id}
                        folder={folder}
                        selected={sameScope(scope, { mode: "folder", folderId: folder.id })}
                        canManage={canManage}
                        onSelect={() => onScopeChange({ mode: "folder", folderId: folder.id })}
                        onRename={onRenameFolder}
                        onDelete={onDeleteFolder}
                    />
                ))}
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0 border-dashed"
                    onClick={onOpenFolderMenu}
                    aria-label={hiddenCount > 0 ? `Folders — ${hiddenCount} more` : "Folders"}
                >
                    {hiddenCount > 0 ? `+${hiddenCount}` : "Folders"}
                </Button>
            </div>
        </div>
    );
}
